import React, { useEffect } from 'react';
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouterProvider } from 'react-router-dom';
import { WarehouseRoutes } from './WarehouseRoutes';
import { AdminLayout, AppLayout, Result } from 'components';
import { ADMIN_ROUTES, ROUTES } from 'constants/routes';
import {
  ClientCommunicationHub,
  ClientManagement,
  General,
  ItemAttachments,
  ItemLogs,
  ItemSuppliers,
  Payments,
  Shipping,
  SupplierCommunicationHub,
  SupplierGeneral,
  SupplierPriceList,
  SupplierPricing,
  SupplierShipping,
} from 'feature';
import { BrandManagement } from 'feature/brandTabs';
import { BrandLogs } from 'feature/brandTabs/logs';
import { ClientLogs } from 'feature/clientTabs/logs';
import {
  Admin,
  AdminForgotPassword,
  AdminResetPassword,
  AdminSignIn,
  Brands,
  Client,
  Clients,
  Leads,
  NotAuthorized,
} from 'pages';
import { Brand } from 'pages/admin/brands/brand';
import { HasAccess } from 'components/common/accessControl';
import { PERMISSIONS } from 'constants/permissions';
import { PriceList } from 'feature/brandTabs/priceList';
import { Countries } from 'pages/admin/countries';
import { Country } from 'pages/admin/countries/country';
import { CountryManagement } from 'feature/countryTabs';
import { CountryLogs } from 'feature/countryTabs/logs';
import { PricingAndSuppliers } from 'feature/brandTabs/pricingAndSuppliers';
import { CountryPricing } from 'feature/countryTabs/pricing';
import { CountryShipping } from 'feature/countryTabs/shipping';
import { BrandAttachments } from 'feature/brandTabs/attachments';
import { Roles } from 'pages/admin/roles';
import { AddEditRole } from 'pages/admin/roles/addEdit';
import { Role } from 'pages/admin/roles/role';
import { Permissions } from 'feature/roleTabs/permissions';
import { RoleLogs } from 'feature/roleTabs/logs';
import { Users } from 'pages/admin/users';
import { UserDetails } from 'pages/admin/users/user';
import { UserAttachments, UserLogs } from 'feature/userTabs';
import { UserAccount } from 'feature/userTabs/account';
import { UserAddEdit } from 'pages/admin/users/addEdit';
import { UserProfilePage } from 'pages/admin/users/user/profilePage';
import { Suppliers } from 'pages/admin/suppliers';
import { Supplier } from 'pages/admin/suppliers/supplier';
import { TasksEntity } from 'pages/admin/tasks';
import { TaskDetails } from 'pages/admin/tasks/task';
import { TaskAttachments, TaskLogs } from 'feature/taskTabs';
import { AddTask } from 'components/common/tasks/add';
import { TaskGeneral } from 'feature/taskTabs/general';
import { SupplierAttachments } from 'feature/supplierTabs/attachments';
import { SupplierLogs } from 'feature/supplierTabs/logs';
import { SupplierManagement } from 'feature/supplierTabs/management';
import { AddLead } from 'pages/admin/leads/lead/add';
import { Lead } from 'pages/admin/leads/lead';
import { LeadAttachments, LeadCommunicationHub, LeadGeneral, LeadLogs } from 'feature/leadTabs';
import { BrandCommunicationHub } from 'feature/brandTabs/communicationHub';
import { Deals } from 'pages/admin/deals';
import { Deal } from 'pages/admin/deals/deal';
import { DealAttachments, DealCommercialOffers, DealCommunicationHub, DealGeneral, DealLogs } from 'feature/dealTabs';
import PasswordSuccess from 'pages/auth/resetPassword/PasswordSuccess';
import { Items } from 'pages/admin/items';
import { Item } from 'pages/admin/items/item';
import { ClientTasks } from 'feature/clientTabs/tasks';
import { ClientTaskDetails } from 'feature/clientTabs/tasks/clientTaskDetails';
import { BrandTasks } from 'feature/brandTabs/tasks';
import { BrandTaskDetails } from 'feature/brandTabs/tasks/brandTaskDetails';
import { SupplierTasks } from 'feature/supplierTabs/tasks';
import { SupplierTaskDetails } from 'feature/supplierTabs/tasks/supplierTaskDetails';
import { CountryTasks } from 'feature/countryTabs/tasks';
import { CountryTaskDetails } from 'feature/countryTabs/tasks/countryTaskDetails';
import { ItemTasks } from 'feature/itemTabs/tasks';
import { ItemTaskDetails } from 'feature/itemTabs/tasks/itemTaskDetails';
import { LeadTasks } from 'feature/leadTabs/tasks';
import { LeadTaskDetails } from 'feature/leadTabs/tasks/leadTaskDetails';
import { DealTasks } from 'feature/dealTabs/tasks';
import { DealTaskDetails } from 'feature/dealTabs/tasks/dealTaskDetails';
import { PurchaseRequests } from 'pages/admin/purchaseRequest';
import { PurchaseRequest } from 'pages/admin/purchaseRequest/purchaseRequest';
import { RequestPrice } from 'feature/purchaseRequestTabs/general/requestPrice';
import { PurchaseRequestAttachments } from 'feature/purchaseRequestTabs';
import { PurchaseRequestGeneral } from 'feature/purchaseRequestTabs/general';
import { SupplierOfferEdit } from 'feature/purchaseRequestTabs/supplierOffers/edit';
import { SupplierOffersTab } from 'feature/purchaseRequestTabs/supplierOffers';
import { PurchaseRequestCommunicationHub } from 'feature/purchaseRequestTabs/communicationHub';
import { PurchaseRequestTasks } from 'feature/purchaseRequestTabs/tasks';
import { PurchaseRequestTaskDetails } from 'feature/purchaseRequestTabs/tasks/purchaseRequestTaskDetails';
import { PurchaseRequestLogs } from 'feature/purchaseRequestTabs/logs';
import { SendOffer } from 'feature/dealTabs/commercialOffers/sendOffer';
import { Dashboard } from 'pages/admin/dashboard';
import { CommercialOfferUpdate } from 'feature/dealTabs/commercialOffers/updateCommercialOfffer';
import { Notification } from 'feature/dashboardTabs/notification';
import { DashboardContent } from 'feature/dashboardTabs/dashboard';
import { Orders } from 'pages/admin/orders';
import { OrderGeneral, OrderLogs } from 'feature/orderTabs';
import { OrderAttachments } from 'feature/orderTabs/attachments';
import { Order } from 'pages/admin/orders/order';
import { PurchaseOrders } from 'pages/admin/purchaseOrders';
import { AddEditPurchaseOrder } from 'pages/admin/purchaseOrders/add';
import { PurchaseOrderAttachments, PurchaseOrderGeneral } from 'feature/purchaseOrderTabs';
import { PurchaseOrderLogs } from 'feature/purchaseOrderTabs/logs';
import { PurchaseOrderTaskDetails } from 'feature/purchaseOrderTabs/tasks/purchaseOrderTaskDetails';
import { PurchaseOrderTasks } from 'feature/purchaseOrderTabs/tasks';
import { PurchaseOrderCommunicationHub } from 'feature/purchaseOrderTabs/communicationHub';
import { PurchaseOrder } from 'pages/admin/purchaseOrders/purchaseOrder';
import { SendPurchaseOrder } from 'feature/purchaseOrderTabs/general/sendOffer/index';
import { PurchaseOrderRequestPrice } from 'pages/admin/purchaseOrders/add/requestPrice';
import { CountryWarehouses } from 'feature/countryTabs/warehouses';
import { SupplierOffers } from 'pages/admin/supplierOffers';
import { OrderTasks } from 'feature/orderTabs/tasks';
import { OrderTaskDetails } from 'feature/orderTabs/tasks/orderTaskDetails';
import { SupplierOfferEditWrapper } from 'pages/admin/supplierOffers/supplierOfferEditWrapper';
import { CustomSupplierOfferEdit } from 'pages/admin/supplierOffers/edit';
import { getDefaultRedirectPath } from '../utils/routeGuards';
// import { PrivateRoutes } from './PrivateRoutes';

// const LazyTeam = lazy(() => import('pages')
//   .then(({Team}) => ({default: Team}))
// );

// Component to handle home route redirection based on user type
const HomeRedirect: React.FC = () => {
  useEffect(() => {
    const defaultPath = getDefaultRedirectPath();
    window.location.replace(defaultPath);
  }, []);

  return null;
};

export const Navigation = React.memo(() => {
  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        {/* Warehouse Routes */}
        <Route path="/auth/warehouse/*" element={<WarehouseRoutes />} />
        <Route path="/warehouse/*" element={<WarehouseRoutes />} />

        <Route element={<AdminLayout />}>
          <Route path={ADMIN_ROUTES.admin} element={<Admin />}>
            {/*TODO this should be private route*/}
            <Route path={ADMIN_ROUTES.dashboardPage} element={<Dashboard />}>
              <Route index element={<Navigate to={ADMIN_ROUTES.dashboardList} replace />} />
              <Route path={ADMIN_ROUTES.dashboardList} element={<DashboardContent />} />
              <Route path={ADMIN_ROUTES.dashboardNotification} element={<Notification />} />
            </Route>
            {/*DEALS*/}
            <Route
              path={ADMIN_ROUTES.deals.list}
              element={
                <HasAccess role={PERMISSIONS.deals.deal.read}>
                  <Deals />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.deals.createTask}
              element={
                <HasAccess role={PERMISSIONS.tasks.task.write}>
                  <AddTask />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.deals.sendCommercialOffers}
              element={
                <HasAccess role={PERMISSIONS.deals.deal.read}>
                  <SendOffer />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.deals.view}/:id/${ADMIN_ROUTES.deals.commercialOfferUpdate}/:commercialOfferId`}
              element={
                <HasAccess role={PERMISSIONS.deals.commercialOffers.write}>
                  <CommercialOfferUpdate />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.deals.view}/:id`}
              element={
                <HasAccess role={PERMISSIONS.deals.deal.read}>
                  <Deal />
                </HasAccess>
              }
            >
              <Route
                path={ADMIN_ROUTES.deals.purchaseRequest}
                element={
                  <HasAccess role={PERMISSIONS.deals.tabs.purchaseRequest.read}>
                    <DealGeneral />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.deals.commercialOffers}
                element={
                  <HasAccess role={PERMISSIONS.deals.tabs.commercialOffers.read}>
                    <DealCommercialOffers />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.deals.communicationHub}
                element={
                  <HasAccess role={PERMISSIONS.deals.tabs.communicationHub.read}>
                    <DealCommunicationHub />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.deals.tasks}
                element={
                  <HasAccess role={PERMISSIONS.deals.tabs.task.read}>
                    <DealTasks />
                  </HasAccess>
                }
              />
              <Route
                path={`${ADMIN_ROUTES.deals.tasks}/:taskId`}
                element={
                  <HasAccess role={PERMISSIONS.tasks.task.read}>
                    <DealTaskDetails />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.deals.attachments}
                element={
                  <HasAccess role={PERMISSIONS.deals.tabs.attachment.read}>
                    <DealAttachments />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.deals.logs}
                element={
                  <HasAccess role={PERMISSIONS.deals.tabs.logs.read}>
                    <DealLogs />
                  </HasAccess>
                }
              />
            </Route>
            {/*LEADS*/}
            <Route
              path={ADMIN_ROUTES.leads.list}
              element={
                <HasAccess role={PERMISSIONS.leads.lead.read}>
                  <Leads />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.leads.list}/add`}
              element={
                <HasAccess role={PERMISSIONS.leads.lead.write}>
                  <AddLead />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.leads.createTask}
              element={
                <HasAccess role={PERMISSIONS.tasks.task.write}>
                  <AddTask />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.leads.view}/:id`}
              element={
                <HasAccess role={PERMISSIONS.leads.lead.read}>
                  <Lead />
                </HasAccess>
              }
            >
              <Route
                path={ADMIN_ROUTES.leads.general}
                element={
                  <HasAccess role={PERMISSIONS.leads.tabs.general.read}>
                    <LeadGeneral />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.leads.communicationHub}
                element={
                  <HasAccess role={PERMISSIONS.leads.tabs.communicationHub.read}>
                    <LeadCommunicationHub />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.leads.tasks}
                element={
                  <HasAccess role={PERMISSIONS.leads.tabs.task.read}>
                    <LeadTasks />
                  </HasAccess>
                }
              />
              <Route
                path={`${ADMIN_ROUTES.leads.tasks}/:taskId`}
                element={
                  <HasAccess role={PERMISSIONS.tasks.task.read}>
                    <LeadTaskDetails />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.leads.attachments}
                element={
                  <HasAccess role={PERMISSIONS.leads.tabs.attachment.read}>
                    <LeadAttachments />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.leads.logs}
                element={
                  <HasAccess role={PERMISSIONS.leads.tabs.logs.read}>
                    <LeadLogs />
                  </HasAccess>
                }
              />
            </Route>
            {/*ORDERS*/}
            <Route
              path={ADMIN_ROUTES.orders.list}
              element={
                <HasAccess role={PERMISSIONS.orders.order.read}>
                  <Orders />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.orders.createTask}
              element={
                <HasAccess role={PERMISSIONS.tasks.task.write}>
                  <AddTask />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.orders.view}/:id`}
              element={
                <HasAccess role={PERMISSIONS.orders.order.read}>
                  <Order />
                </HasAccess>
              }
            >
              <Route
                path={ADMIN_ROUTES.orders.general}
                element={
                  <HasAccess role={PERMISSIONS.orders.tabs.general.read}>
                    <OrderGeneral />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.orders.tasks}
                element={
                  <HasAccess role={PERMISSIONS.orders.tabs.task.read}>
                    <OrderTasks />
                  </HasAccess>
                }
              />
              <Route
                path={`${ADMIN_ROUTES.orders.tasks}/:taskId`}
                element={
                  <HasAccess role={PERMISSIONS.tasks.task.read}>
                    <OrderTaskDetails />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.orders.logs}
                element={
                  <HasAccess role={PERMISSIONS.orders.tabs.logs.read}>
                    <OrderLogs />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.orders.attachments}
                element={
                  <HasAccess role={PERMISSIONS.orders.tabs.attachment.read}>
                    <OrderAttachments />
                  </HasAccess>
                }
              />
            </Route>
            {/*CLIENTS*/}
            <Route
              path={ADMIN_ROUTES.clients}
              element={
                <HasAccess role={PERMISSIONS.clients.client.read}>
                  <Clients />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.clientTaskCreate}
              element={
                <HasAccess role={PERMISSIONS.tasks.task.write}>
                  <AddTask />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.client}/:id`}
              element={
                <HasAccess role={PERMISSIONS.clients.client.read}>
                  <Client />
                </HasAccess>
              }
            >
              <Route
                path={ADMIN_ROUTES.clientGeneral}
                element={
                  <HasAccess role={PERMISSIONS.clients.tabs.general.read}>
                    <General />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.clientManagement}
                element={
                  <HasAccess role={PERMISSIONS.clients.tabs.management.read}>
                    <ClientManagement />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.clientShipping}
                element={
                  <HasAccess role={PERMISSIONS.clients.tabs.shipping.read}>
                    <Shipping />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.clientTasks}
                element={
                  <HasAccess role={PERMISSIONS.clients.tabs.task.read}>
                    <ClientTasks />
                  </HasAccess>
                }
              />
              <Route
                path={`${ADMIN_ROUTES.clientTasks}/:taskId`}
                element={
                  <HasAccess role={PERMISSIONS.tasks.task.read}>
                    <ClientTaskDetails />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.clientLogs}
                element={
                  <HasAccess role={PERMISSIONS.clients.tabs.logs.read}>
                    <ClientLogs />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.payments}
                element={
                  <HasAccess role={PERMISSIONS.clients.tabs.payments.read}>
                    <Payments />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.communicationHub}
                element={
                  <HasAccess role={PERMISSIONS.clients.tabs.communicationHub.read}>
                    <ClientCommunicationHub />
                  </HasAccess>
                }
              />
            </Route>
            {/*BRANDS*/}
            <Route
              path={ADMIN_ROUTES.brand.list}
              element={
                <HasAccess role={PERMISSIONS.brands.brand.read}>
                  <Brands />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.brand.createTask}
              element={
                <HasAccess role={PERMISSIONS.tasks.task.write}>
                  <AddTask />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.brand.view}/:id`}
              element={
                <HasAccess role={PERMISSIONS.brands.brand.read}>
                  <Brand />
                </HasAccess>
              }
            >
              <Route
                path={ADMIN_ROUTES.brand.management}
                element={
                  <HasAccess role={PERMISSIONS.brands.tabs.management.read}>
                    <BrandManagement />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.brand.pricingAndSuppliers}
                element={
                  <HasAccess role={PERMISSIONS.brands.tabs.pricing_and_suppliers.read}>
                    <PricingAndSuppliers />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.brand.logs}
                element={
                  <HasAccess role={PERMISSIONS.brands.tabs.logs.read}>
                    <BrandLogs />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.brand.tasks}
                element={
                  <HasAccess role={PERMISSIONS.brands.tabs.task.read}>
                    <BrandTasks />
                  </HasAccess>
                }
              />
              <Route
                path={`${ADMIN_ROUTES.brand.tasks}/:taskId`}
                element={
                  <HasAccess role={PERMISSIONS.tasks.task.read}>
                    <BrandTaskDetails />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.brand.pricelists}
                element={
                  <HasAccess role={PERMISSIONS.brands.tabs.pricelist.read}>
                    <PriceList />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.brand.communicationHub}
                element={
                  <HasAccess role={PERMISSIONS.brands.tabs.communicationHub.read}>
                    <BrandCommunicationHub />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.brand.attachments}
                element={
                  <HasAccess role={PERMISSIONS.brands.tabs.attachment.read}>
                    <BrandAttachments />
                  </HasAccess>
                }
              />
              {/* ↓↓↓ Leave this as the last ROUTE path and remove it after all user screens are developed. ↓↓↓ */}
              <Route path={'*'} element={<Result status="warning" title="Sorry, the page is under construction." />} />
            </Route>
            {/*COUNTRIES*/}
            <Route
              path={ADMIN_ROUTES.country.list}
              element={
                <HasAccess role={PERMISSIONS.countries.country.read}>
                  <Countries />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.country.createTask}
              element={
                <HasAccess role={PERMISSIONS.tasks.task.write}>
                  <AddTask />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.country.view}/:id`}
              element={
                <HasAccess role={PERMISSIONS.countries.country.read}>
                  <Country />
                </HasAccess>
              }
            >
              <Route
                path={ADMIN_ROUTES.country.pricing}
                element={
                  <HasAccess role={PERMISSIONS.countries.tabs.pricing.read}>
                    <CountryPricing />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.country.shipping}
                element={
                  <HasAccess role={PERMISSIONS.countries.tabs.shipping.read}>
                    <CountryShipping />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.country.warehouses}
                element={
                  <HasAccess role={PERMISSIONS.countries.tabs.warehouse.read}>
                    <CountryWarehouses />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.country.management}
                element={
                  <HasAccess role={PERMISSIONS.countries.tabs.management.read}>
                    <CountryManagement />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.country.tasks}
                element={
                  <HasAccess role={PERMISSIONS.countries.tabs.task.read}>
                    <CountryTasks />
                  </HasAccess>
                }
              />
              <Route
                path={`${ADMIN_ROUTES.country.tasks}/:taskId`}
                element={
                  <HasAccess role={PERMISSIONS.tasks.task.read}>
                    <CountryTaskDetails />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.country.logs}
                element={
                  <HasAccess role={PERMISSIONS.countries.tabs.logs.read}>
                    <CountryLogs />
                  </HasAccess>
                }
              />
            </Route>
            {/*USERS*/}
            <Route
              path={ADMIN_ROUTES.users.list}
              element={
                <HasAccess role={PERMISSIONS.users.user.read}>
                  <Users />
                </HasAccess>
              }
            ></Route>
            <Route
              path={ADMIN_ROUTES.users.create}
              element={
                <HasAccess role={PERMISSIONS.users.user.write}>
                  <UserAddEdit />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.users.update}/:id`}
              element={
                <HasAccess role={PERMISSIONS.users.user.write}>
                  <UserAddEdit />
                </HasAccess>
              }
            />
            <Route path={ADMIN_ROUTES.adminProfile} element={<UserProfilePage />} />
            <Route
              path={`${ADMIN_ROUTES.users.view}/:id`}
              element={
                <HasAccess role={PERMISSIONS.users.user.read}>
                  <UserDetails />
                </HasAccess>
              }
            >
              <Route
                path={ADMIN_ROUTES.users.account}
                element={
                  <HasAccess role={PERMISSIONS.users.tabs.account.read}>
                    <UserAccount />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.users.attachments}
                element={
                  <HasAccess role={PERMISSIONS.users.tabs.attachment.read}>
                    <UserAttachments />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.users.logs}
                element={
                  <HasAccess role={PERMISSIONS.users.tabs.logs.read}>
                    <UserLogs />
                  </HasAccess>
                }
              />
            </Route>
            {/*Roles and permissions*/}
            <Route
              path={ADMIN_ROUTES.role.list}
              element={
                <HasAccess role={PERMISSIONS.roles.role.read}>
                  <Roles />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.role.create}
              element={
                <HasAccess role={PERMISSIONS.roles.role.write}>
                  <AddEditRole />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.role.update}/:id`}
              element={
                <HasAccess role={PERMISSIONS.roles.role.write}>
                  <AddEditRole />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.role.view}/:id`}
              element={
                <HasAccess role={PERMISSIONS.roles.role.read}>
                  <Role />
                </HasAccess>
              }
            >
              <Route
                path={ADMIN_ROUTES.role.permissions}
                element={
                  <HasAccess role={PERMISSIONS.roles.tabs.permissions.read}>
                    <Permissions />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.role.logs}
                element={
                  <HasAccess role={PERMISSIONS.roles.tabs.logs.read}>
                    <RoleLogs />
                  </HasAccess>
                }
              />
            </Route>
            <Route
              path={ADMIN_ROUTES.suppliers.list}
              element={
                <HasAccess role={PERMISSIONS.suppliers.supplier.read}>
                  <Suppliers />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.suppliers.createTask}
              element={
                <HasAccess role={PERMISSIONS.tasks.task.write}>
                  <AddTask />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.suppliers.view}/:id`}
              element={
                <HasAccess role={PERMISSIONS.suppliers.supplier.read}>
                  <Supplier />
                </HasAccess>
              }
            >
              <Route
                path={ADMIN_ROUTES.suppliers.general}
                element={
                  <HasAccess role={PERMISSIONS.suppliers.tabs.general.read}>
                    <SupplierGeneral />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.suppliers.priceLists}
                element={
                  <HasAccess role={PERMISSIONS.suppliers.tabs.priceList.read}>
                    <SupplierPriceList />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.suppliers.communicationHub}
                element={
                  <HasAccess role={PERMISSIONS.suppliers.tabs.communicationHub.read}>
                    <SupplierCommunicationHub />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.suppliers.tasks}
                element={
                  <HasAccess role={PERMISSIONS.suppliers.tabs.task.read}>
                    <SupplierTasks />
                  </HasAccess>
                }
              />
              <Route
                path={`${ADMIN_ROUTES.suppliers.tasks}/:taskId`}
                element={
                  <HasAccess role={PERMISSIONS.tasks.task.read}>
                    <SupplierTaskDetails />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.suppliers.shipping}
                element={
                  <HasAccess role={PERMISSIONS.suppliers.tabs.shipping.read}>
                    <SupplierShipping />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.suppliers.management}
                element={
                  <HasAccess role={PERMISSIONS.suppliers.tabs.management.read}>
                    <SupplierManagement />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.suppliers.attachments}
                element={
                  <HasAccess role={PERMISSIONS.suppliers.tabs.attachment.read}>
                    <SupplierAttachments />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.suppliers.logs}
                element={
                  <HasAccess role={PERMISSIONS.suppliers.tabs.logs.read}>
                    <SupplierLogs />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.suppliers.pricing}
                element={
                  <HasAccess role={PERMISSIONS.suppliers.tabs.pricing.read}>
                    <SupplierPricing />
                  </HasAccess>
                }
              />
            </Route>
            {/*Tasks*/}
            <Route
              path={ADMIN_ROUTES.tasks.list}
              element={
                <HasAccess role={PERMISSIONS.tasks.task.read}>
                  <TasksEntity />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.tasks.create}
              element={
                <HasAccess role={PERMISSIONS.tasks.task.write}>
                  <AddTask />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.tasks.view}/:id`}
              element={
                <HasAccess role={PERMISSIONS.tasks.task.read}>
                  <TaskDetails />
                </HasAccess>
              }
            >
              <Route
                path={ADMIN_ROUTES.tasks.general}
                element={
                  <HasAccess role={PERMISSIONS.tasks.tabs.general.read}>
                    <TaskGeneral />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.tasks.attachments}
                element={
                  <HasAccess role={PERMISSIONS.tasks.tabs.attachment.read}>
                    <TaskAttachments />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.tasks.logs}
                element={
                  <HasAccess role={PERMISSIONS.tasks.tabs.logs.read}>
                    <TaskLogs />
                  </HasAccess>
                }
              />
            </Route>
            {/*Items*/}
            <Route
              path={ADMIN_ROUTES.items.list}
              element={
                <HasAccess role={PERMISSIONS.items.item.read}>
                  <Items />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.items.createTask}
              element={
                <HasAccess role={PERMISSIONS.tasks.task.write}>
                  <AddTask />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.items.view}/:id`}
              element={
                <HasAccess role={PERMISSIONS.items.item.read}>
                  <Item />
                </HasAccess>
              }
            >
              <Route
                path={ADMIN_ROUTES.items.suppliers}
                element={
                  <HasAccess role={PERMISSIONS.items.tabs.suppliers.read}>
                    <ItemSuppliers />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.items.tasks}
                element={
                  <HasAccess role={PERMISSIONS.items.tabs.task.read}>
                    <ItemTasks />
                  </HasAccess>
                }
              />
              <Route
                path={`${ADMIN_ROUTES.items.tasks}/:taskId`}
                element={
                  <HasAccess role={PERMISSIONS.tasks.task.read}>
                    <ItemTaskDetails />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.items.attachments}
                element={
                  <HasAccess role={PERMISSIONS.items.tabs.attachment.read}>
                    <ItemAttachments />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.items.logs}
                element={
                  <HasAccess role={PERMISSIONS.items.tabs.logs.read}>
                    <ItemLogs />
                  </HasAccess>
                }
              />
            </Route>
            {/*PurchaseRequest*/}
            <Route
              path={ADMIN_ROUTES.purchaseRequest.list}
              element={
                <HasAccess role={PERMISSIONS.purchaseRequests.purchaseRequest.read}>
                  <PurchaseRequests />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.purchaseRequest.requestPrice}
              element={
                <HasAccess role={PERMISSIONS.purchaseRequests.tabs.general.read}>
                  <RequestPrice />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.purchaseRequest.view}/:id/${ADMIN_ROUTES.purchaseRequest.supplierOfferUpdate}`}
              element={
                <HasAccess role={PERMISSIONS.purchaseRequests.supplierOffers.write}>
                  <SupplierOfferEdit />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.purchaseRequest.createTask}
              element={
                <HasAccess role={PERMISSIONS.tasks.task.write}>
                  <AddTask />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.purchaseRequest.view}/:id`}
              element={
                <HasAccess role={PERMISSIONS.purchaseRequests.purchaseRequest.read}>
                  <PurchaseRequest />
                </HasAccess>
              }
            >
              <Route
                path={ADMIN_ROUTES.purchaseRequest.general}
                element={
                  <HasAccess role={PERMISSIONS.purchaseRequests.tabs.general.read}>
                    <PurchaseRequestGeneral />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.purchaseRequest.communicationHub}
                element={
                  <HasAccess role={PERMISSIONS.purchaseRequests.tabs.communicationHub.read}>
                    <PurchaseRequestCommunicationHub />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.purchaseRequest.tasks}
                element={
                  <HasAccess role={PERMISSIONS.purchaseRequests.tabs.task.read}>
                    <PurchaseRequestTasks />
                  </HasAccess>
                }
              />
              <Route
                path={`${ADMIN_ROUTES.purchaseRequest.tasks}/:taskId`}
                element={
                  <HasAccess role={PERMISSIONS.tasks.task.read}>
                    <PurchaseRequestTaskDetails />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.purchaseRequest.attachments}
                element={
                  <HasAccess role={PERMISSIONS.purchaseRequests.tabs.attachment.read}>
                    <PurchaseRequestAttachments />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.purchaseRequest.supplierOffers}
                element={
                  <HasAccess role={PERMISSIONS.purchaseRequests.tabs.supplierOffers.read}>
                    <SupplierOffersTab />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.purchaseRequest.logs}
                element={
                  <HasAccess role={PERMISSIONS.purchaseRequests.tabs.logs.read}>
                    <PurchaseRequestLogs />
                  </HasAccess>
                }
              />
            </Route>
            <Route
              path={ADMIN_ROUTES.purchaseOrder.list}
              element={
                <HasAccess role={PERMISSIONS.purchaseOrders.purchaseOrder.read}>
                  <PurchaseOrders />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.purchaseOrder.create}
              element={
                <HasAccess role={PERMISSIONS.purchaseOrders.purchaseOrder.write}>
                  <AddEditPurchaseOrder />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.purchaseOrder.requestPrice}
              element={
                <HasAccess role={PERMISSIONS.purchaseOrders.purchaseOrder.write}>
                  <PurchaseOrderRequestPrice />
                </HasAccess>
              }
            />
            <Route
              path={`${ADMIN_ROUTES.purchaseOrder.view}/:id`}
              element={
                <HasAccess role={PERMISSIONS.purchaseOrders.purchaseOrder.read}>
                  <PurchaseOrder />
                </HasAccess>
              }
            >
              <Route
                path={ADMIN_ROUTES.purchaseOrder.general}
                element={
                  <HasAccess role={PERMISSIONS.purchaseOrders.tabs.general.read}>
                    <PurchaseOrderGeneral />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.purchaseOrder.communicationHub}
                element={
                  <HasAccess role={PERMISSIONS.purchaseOrders.tabs.communicationHub.read}>
                    <PurchaseOrderCommunicationHub />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.purchaseOrder.tasks}
                element={
                  <HasAccess role={PERMISSIONS.purchaseOrders.tabs.task.read}>
                    <PurchaseOrderTasks />
                  </HasAccess>
                }
              />
              <Route
                path={`${ADMIN_ROUTES.purchaseOrder.tasks}/:taskId`}
                element={
                  <HasAccess role={PERMISSIONS.tasks.task.read}>
                    <PurchaseOrderTaskDetails />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.purchaseOrder.attachments}
                element={
                  <HasAccess role={PERMISSIONS.purchaseOrders.tabs.attachment.read}>
                    <PurchaseOrderAttachments />
                  </HasAccess>
                }
              />
              <Route
                path={ADMIN_ROUTES.purchaseOrder.logs}
                element={
                  <HasAccess role={PERMISSIONS.purchaseOrders.tabs.logs.read}>
                    <PurchaseOrderLogs />
                  </HasAccess>
                }
              />
            </Route>
            {/* Send Purchase Order Route */}
            <Route
              path={`${ADMIN_ROUTES.purchaseOrder.view}/:id/send-order`}
              element={
                <HasAccess role={PERMISSIONS.purchaseOrders.purchaseOrder.write}>
                  <SendPurchaseOrder />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.purchaseOrder.supplierOfferUpdate}
              element={
                <HasAccess role={PERMISSIONS.purchaseOrders.supplierOffer.write}>
                  <CustomSupplierOfferEdit />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.supplierOffer.list}
              element={
                <HasAccess role={PERMISSIONS.supplierOffers.supplierOffer.read}>
                  <SupplierOffers />
                </HasAccess>
              }
            />
            <Route
              path={ADMIN_ROUTES.supplierOffer.supplierOfferUpdate}
              element={
                <HasAccess role={PERMISSIONS.supplierOffers.supplierOffer.write}>
                  <SupplierOfferEditWrapper />
                </HasAccess>
              }
            />
          </Route>
        </Route>
        <Route element={<AppLayout />}>
          <Route path={ADMIN_ROUTES.adminSignIn} element={<AdminSignIn />} />
          <Route path={ADMIN_ROUTES.adminResetPassword} element={<AdminResetPassword />} />
          <Route path={ADMIN_ROUTES.adminResetPasswordSuccess} element={<PasswordSuccess />} />
          <Route path={ADMIN_ROUTES.adminForgotPassword} element={<AdminForgotPassword />} />
        </Route>
        <Route path={ROUTES.notAuthorized} element={<NotAuthorized />} />
        <Route path={ROUTES.home} element={<HomeRedirect />} />
        <Route path={'*'} element={<Navigate to={ADMIN_ROUTES.adminSignIn} replace />} />
      </>
    ),
    {
      // check this future flags behaviour if need
      future: {
        v7_relativeSplatPath: true,
        v7_fetcherPersist: true,
        v7_normalizeFormMethod: true,
        v7_partialHydration: true,
        v7_skipActionErrorRevalidation: true,
      },
    }
  );
  return (
    <RouterProvider
      future={{
        v7_startTransition: true,
      }}
      router={router}
    />
  );
});
